'use client'

import { useEffect, useMemo, useState } from 'react'
import { Loader2, X, KeyRound, Check } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../ProcessedEntry/DomainEntry/processedTypes'
import { apiGet, apiPatch, apiPost, useCatalog, useSources, useTenants } from './entriesApi'
import type { TenantAccessItem, TenantItem } from './entryTypes'
import { trackEvent } from '@/lib/analytics'
import { SearchableMultiSelect } from '@shared/searchable-multi-select'
import { ManageEntityView, ManageEntityField, type ManageEntitySubmitResult } from './ManageEntityView'
import { brandsForPlatforms, grantableDashboards } from '../dashboardCatalog'

type TenantForm = {
  id?: string
  nameCode: string
  name: string
  nameLong: string
  description: string
  logoUrl: string
  isActive: boolean
  platformIds: number[]
  sourceIds: string[]
}

function TenantAccessPanel({ tenantId, theme }: { tenantId: string; theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  const [grants, setGrants] = useState<TenantAccessItem[]>([])
  const [loading, setLoading] = useState(true)
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'saving'>('idle')
  const [error, setError] = useState<string | undefined>()
  const [newEmail, setNewEmail] = useState('')
  const [createStatus, setCreateStatus] = useState<'idle' | 'saving'>('idle')
  const [createError, setCreateError] = useState<string | undefined>()
  const [createNotice, setCreateNotice] = useState<string | undefined>()

  const load = async () => {
    setLoading(true)
    const result = await apiGet<TenantAccessItem[]>(`/tenants/${tenantId}/access`)
    if (result.success && result.data) setGrants(result.data)
    setLoading(false)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId])

  const grantAccess = async () => {
    if (!email.trim()) return
    setStatus('saving')
    setError(undefined)
    const result = await apiPost<TenantAccessItem>(`/tenants/${tenantId}/access`, { email: email.trim() })
    setStatus('idle')
    if (!result.success) {
      setError(typeof result.error === 'string' ? result.error : 'Failed to grant access')
      return
    }
    setEmail('')
    load()
  }

  const revokeAccess = async (accessId: string) => {
    await apiPost(`/tenants/${tenantId}/access/${accessId}`, {})
    load()
  }

  const [resetSendingId, setResetSendingId] = useState<string | null>(null)
  const [resetSentId, setResetSentId] = useState<string | null>(null)

  const sendResetPassword = async (accessId: string) => {
    setResetSendingId(accessId)
    setResetSentId(null)
    const result = await apiPost(`/tenants/${tenantId}/access/${accessId}/reset-password`, {})
    setResetSendingId(null)
    if (result.success) {
      setResetSentId(accessId)
      setTimeout(() => setResetSentId((id) => (id === accessId ? null : id)), 4000)
    } else {
      setError(typeof result.error === 'string' ? result.error : 'Failed to send reset email')
    }
  }

  // For a user who's never been through webapp's own public sign-up (which
  // is where the users table normally gets populated) — creates a real
  // SuperTokens account (no admin-set password; the backend generates a
  // placeholder and emails the user a link to set up their own, reusing the
  // reset-password token mechanism) and grants it to this tenant in one
  // step, instead of "Grant Access" above, which only attaches existing users.
  const createUser = async () => {
    if (!newEmail.trim()) return
    setCreateStatus('saving')
    setCreateError(undefined)
    setCreateNotice(undefined)
    const result = await apiPost<{ setupEmailSent?: boolean; setupEmailError?: string }>(`/tenants/${tenantId}/users`, { email: newEmail.trim() })
    setCreateStatus('idle')
    if (!result.success) {
      setCreateError(typeof result.error === 'string' ? result.error : 'Failed to create user')
      return
    }
    if (result.data?.setupEmailSent === false) {
      setCreateNotice(`User created, but the setup email failed to send: ${result.data.setupEmailError ?? 'unknown error'}`)
    } else {
      setCreateNotice('User created — a setup email was sent so they can choose their password.')
    }
    setNewEmail('')
    load()
  }

  return (
    <div>
      <div className="mb-2 flex gap-2">
        <Input
          className={c.input}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="user@example.com"
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), grantAccess())}
        />
        <button
          type="button"
          onClick={grantAccess}
          disabled={status === 'saving' || !email.trim()}
          className={`flex shrink-0 items-center gap-1 rounded border px-3 py-1 text-[12px] font-medium disabled:opacity-50 ${c.line} ${c.faint}`}
        >
          {status === 'saving' && <Loader2 className="h-3 w-3 animate-spin" />}
          Grant Access
        </button>
      </div>
      {error && <div className="mb-2 text-[11px] text-destructive">{error}</div>}

      <div className={`mb-3 rounded border p-2 ${c.line}`}>
        <div className={`mb-1.5 text-[10px] font-medium uppercase tracking-wide ${c.faint}`}>Or create a new user</div>
        <div className="flex gap-2">
          <Input
            className={c.input}
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="user@example.com"
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), createUser())}
          />
          <button
            type="button"
            onClick={createUser}
            disabled={createStatus === 'saving' || !newEmail.trim()}
            className={`flex shrink-0 items-center gap-1 rounded border px-3 py-1 text-[12px] font-medium disabled:opacity-50 ${c.line} ${c.faint}`}
          >
            {createStatus === 'saving' && <Loader2 className="h-3 w-3 animate-spin" />}
            Create user
          </button>
        </div>
        {createError && <div className="mt-1.5 text-[11px] text-destructive">{createError}</div>}
        {createNotice && <div className={`mt-1.5 text-[11px] ${createNotice.startsWith('User created —') ? 'text-success' : 'text-warning'}`}>{createNotice}</div>}
        <div className={`mt-1 text-[10px] ${c.faint}`}>Creates a SuperTokens account (pre-verified) and grants it to this tenant directly — no password to set here, the user gets an email to choose their own.</div>
      </div>

      {loading ? (
        <div className={`text-[12px] ${c.faint}`}>Loading…</div>
      ) : grants.length === 0 ? (
        <div className={`text-[12px] ${c.faint}`}>No users have access to this tenant yet.</div>
      ) : (
        <ul className="space-y-1">
          {grants.map((g) => (
            <li key={g.id} className={`flex items-center justify-between rounded border px-2 py-1 text-[12px] ${c.line}`}>
              <span>
                {g.email ?? g.user_id}
                {g.email_verified === false && <span className="ml-1 text-warning">(unverified)</span>}
              </span>
              <span className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => sendResetPassword(g.id)}
                  disabled={resetSendingId === g.id}
                  title="Send this user a password reset email"
                  className={`flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] font-medium disabled:opacity-50 ${c.line} ${resetSentId === g.id ? 'border-success text-success' : `${c.faint} hover:opacity-70`}`}
                >
                  {resetSendingId === g.id ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : resetSentId === g.id ? (
                    <Check className="h-3 w-3" />
                  ) : (
                    <KeyRound className="h-3 w-3" />
                  )}
                  {resetSentId === g.id ? 'Sent!' : 'Reset password'}
                </button>
                <button type="button" onClick={() => revokeAccess(g.id)} title="Revoke access" className={`rounded p-0.5 hover:opacity-70 ${c.faint}`}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className={`mt-1 text-[10px] ${c.faint}`}>Grants access to this tenant's scoped data for the given user's account.</div>
    </div>
  )
}

// The matrix, one tenant at a time: which backoffice dashboards this
// tenant's users (granted above, in TenantAccessPanel) can see. The same
// dashboard can be checked in for several different tenants independently —
// there's no ownership/exclusivity between them.
function TenantDashboardAccessPanel({ tenantId, theme }: { tenantId: string; theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  const { catalog } = useCatalog()
  const [granted, setGranted] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [error, setError] = useState<string | undefined>()

  const load = async () => {
    setLoading(true)
    const result = await apiGet<string[]>(`/tenants/${tenantId}/dashboard-access`)
    if (result.success && result.data) setGranted(new Set(result.data))
    setLoading(false)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId])

  const toggle = async (key: string) => {
    const next = new Set(granted)
    next.has(key) ? next.delete(key) : next.add(key)
    setSavingKey(key)
    setError(undefined)
    const result = await apiPost(`/tenants/${tenantId}/dashboard-access`, { dashboardKeys: Array.from(next) })
    setSavingKey(null)
    if (!result.success) {
      setError(typeof result.error === 'string' ? result.error : 'Failed to update')
      return
    }
    setGranted(next)
  }

  // Built from the platform table, not only the hand-written brands: a brand
  // generated from a platform row must be grantable here too, or it would be
  // editor-only forever and no tenant could ever be given its dashboards.
  const grantable = useMemo(() => grantableDashboards(brandsForPlatforms(catalog.platforms)), [catalog.platforms])

  const byBrand = new Map<string, typeof grantable>()
  for (const d of grantable) {
    if (!byBrand.has(d.brandLabel)) byBrand.set(d.brandLabel, [])
    byBrand.get(d.brandLabel)!.push(d)
  }

  return (
    <div>
      {loading ? (
        <div className={`text-[12px] ${c.faint}`}>Loading…</div>
      ) : grantable.length === 0 ? (
        <div className={`text-[12px] ${c.faint}`}>No dashboards are grantable yet.</div>
      ) : (
        Array.from(byBrand.entries()).map(([brandLabel, items]) => {
          const ungrouped = items.filter((d) => !d.sectionLabel)
          const bySection = new Map<string, typeof items>()
          for (const d of items) {
            if (!d.sectionLabel) continue
            if (!bySection.has(d.sectionLabel)) bySection.set(d.sectionLabel, [])
            bySection.get(d.sectionLabel)!.push(d)
          }
          return (
            <div key={brandLabel} className="mb-2">
              <div className={`mb-1 text-[10px] font-medium uppercase tracking-wide ${c.faint}`}>{brandLabel}</div>
              <div className="space-y-1">
                {ungrouped.map((d) => (
                  <label key={d.key} className="flex items-center gap-2 text-[12px]">
                    <input
                      type="checkbox"
                      checked={granted.has(d.key)}
                      disabled={savingKey === d.key}
                      onChange={() => toggle(d.key)}
                    />
                    {d.label}
                  </label>
                ))}
              </div>
              {Array.from(bySection.entries()).map(([sectionLabel, sectionItems]) => (
                <div key={sectionLabel} className="mt-1.5 pl-2">
                  <div className={`mb-1 text-[9px] uppercase tracking-wide opacity-70 ${c.faint}`}>{sectionLabel}</div>
                  <div className="space-y-1">
                    {sectionItems.map((d) => (
                      <label key={d.key} className="flex items-center gap-2 text-[12px]">
                        <input
                          type="checkbox"
                          checked={granted.has(d.key)}
                          disabled={savingKey === d.key}
                          onChange={() => toggle(d.key)}
                        />
                        {d.label}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )
        })
      )}
      {error && <div className="mt-1 text-[11px] text-destructive">{error}</div>}
      <div className={`mt-1 text-[10px] ${c.faint}`}>Which dashboards this tenant's granted users can see. The same dashboard can be granted to multiple tenants.</div>
    </div>
  )
}

export function ManageTenantsView({ theme }: { theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  const { tenants, loading: tenantsLoading, refetch: refetchTenants } = useTenants()
  const { catalog, loading: catalogLoading, refetch: refetchCatalog } = useCatalog()
  const { sources, loading: sourcesLoading, refetch: refetchSources } = useSources()

  const emptyForm: TenantForm = { nameCode: '', name: '', nameLong: '', description: '', logoUrl: '', isActive: true, platformIds: [], sourceIds: [] }

  const formFromItem = (t: TenantItem): TenantForm => ({
    id: t.id,
    nameCode: t.name_code,
    name: t.name,
    nameLong: t.name_long ?? '',
    description: t.description ?? '',
    logoUrl: t.logo_url ?? '',
    isActive: t.is_active !== false,
    platformIds: catalog.platforms.filter((p) => p.tenant_ids?.includes(t.id)).map((p) => p.id),
    sourceIds: sources.filter((s) => s.tenant_ids?.includes(t.id)).map((s) => s.id),
  })

  const onSubmit = async (form: TenantForm, mode: 'create' | 'edit', id: string | number | undefined): Promise<ManageEntitySubmitResult> => {
    const payload = {
      nameCode: form.nameCode,
      name: form.name,
      nameLong: form.nameLong || null,
      description: form.description || null,
      logoUrl: form.logoUrl || null,
      isActive: form.isActive,
    }

    let tenantId = id
    if (mode === 'create') {
      const createResult = await apiPost<{ id: string }>('/tenants', payload)
      if (!createResult.success) {
        const errorMessage = typeof createResult.error === 'string' ? createResult.error : 'Failed to create'
        trackEvent({ event_type: 'action', event_name: 'register_tenant_submit', component: 'manage_tenant_form', field_1_key: 'step', field_1_value: 'error', field_2_key: 'message', field_2_value: errorMessage.slice(0, 200) })
        return { success: false, error: errorMessage }
      }
      tenantId = createResult.data?.id
    } else {
      const updateResult = await apiPatch('/tenants', { ...payload, id })
      if (!updateResult.success) {
        const errorMessage = typeof updateResult.error === 'string' ? updateResult.error : 'Failed to update'
        trackEvent({ event_type: 'action', event_name: 'update_tenant_submit', component: 'manage_tenant_form', field_1_key: 'step', field_1_value: 'error', field_2_key: 'message', field_2_value: errorMessage.slice(0, 200) })
        return { success: false, error: errorMessage }
      }
    }

    if (tenantId && (mode === 'create' ? form.platformIds.length > 0 : true)) {
      const assignResult = await apiPost('/tenant-platforms', { tenantId, platformIds: form.platformIds })
      if (!assignResult.success) {
        const errorMessage = typeof assignResult.error === 'string' ? assignResult.error : 'Failed to assign platforms'
        trackEvent({ event_type: 'action', event_name: mode === 'create' ? 'register_tenant_submit' : 'update_tenant_submit', component: 'manage_tenant_form', field_1_key: 'step', field_1_value: 'error' })
        return { success: false, error: `Tenant ${mode === 'create' ? 'created' : 'updated'}, but platform assignment failed: ${errorMessage}` }
      }
    }

    if (tenantId && (mode === 'create' ? form.sourceIds.length > 0 : true)) {
      const assignResult = await apiPost('/tenant-sources', { tenantId, sourceIds: form.sourceIds })
      if (!assignResult.success) {
        const errorMessage = typeof assignResult.error === 'string' ? assignResult.error : 'Failed to assign sources'
        trackEvent({ event_type: 'action', event_name: mode === 'create' ? 'register_tenant_submit' : 'update_tenant_submit', component: 'manage_tenant_form', field_1_key: 'step', field_1_value: 'error' })
        return { success: false, error: `Tenant ${mode === 'create' ? 'created' : 'updated'}, but source assignment failed: ${errorMessage}` }
      }
    }

    trackEvent({ event_type: 'action', event_name: mode === 'create' ? 'register_tenant_submit' : 'update_tenant_submit', component: 'manage_tenant_form', field_1_key: 'step', field_1_value: 'success' })
    return { success: true, id: tenantId }
  }

  return (
    <ManageEntityView<TenantItem, TenantForm>
      theme={theme}
      title="Tenants"
      createLabel="Create Tenant"
      items={tenants}
      loading={tenantsLoading}
      getId={(t) => t.id}
      getName={(t) => t.name}
      getCode={(t) => t.name_code}
      getCreatedAt={(t) => t.created_at}
      getUpdatedAt={(t) => t.updated_at}
      renderListMeta={(t) => t.is_active === false && (
        <span className="rounded bg-danger-surface px-1 text-[9px] font-semibold uppercase text-destructive">Inactive</span>
      )}
      emptyForm={emptyForm}
      formFromItem={formFromItem}
      isFormValid={(form) => !!form.nameCode && !!form.name}
      invalidMessage="Fill in Name Code and Name"
      onSubmit={onSubmit}
      onAfterSuccess={() => { refetchTenants(); refetchCatalog(); refetchSources() }}
      renderFields={(form, setForm) => (
        <>
          <ManageEntityField label="Name Code *" theme={theme}>
            <Input className={c.input} value={form.nameCode} onChange={(e) => setForm((p) => p && { ...p, nameCode: e.target.value })} placeholder="acme-bank" />
          </ManageEntityField>
          <ManageEntityField label="Name *" theme={theme}>
            <Input className={c.input} value={form.name} onChange={(e) => setForm((p) => p && { ...p, name: e.target.value })} />
          </ManageEntityField>
          <ManageEntityField label="Name Long" theme={theme}>
            <Input className={c.input} value={form.nameLong} onChange={(e) => setForm((p) => p && { ...p, nameLong: e.target.value })} />
          </ManageEntityField>
          <ManageEntityField label="Description" theme={theme}>
            <Textarea className={c.input} value={form.description} onChange={(e) => setForm((p) => p && { ...p, description: e.target.value })} />
          </ManageEntityField>
          <ManageEntityField label="Logo URL" theme={theme}>
            <Input className={c.input} value={form.logoUrl} onChange={(e) => setForm((p) => p && { ...p, logoUrl: e.target.value })} />
          </ManageEntityField>
          {form.logoUrl && <img src={form.logoUrl} alt="" className="mx-auto mb-3 h-16 w-16 object-contain" />}

          <ManageEntityField label="Platforms" theme={theme}>
            <SearchableMultiSelect
              theme={theme}
              loading={catalogLoading}
              placeholder="Select platforms…"
              searchPlaceholder="Search by code or name…"
              options={catalog.platforms.map((p) => ({ id: p.id, code: p.name_code, label: p.is_public === false ? `${p.name} (hidden)` : p.name }))}
              selectedIds={form.platformIds}
              onChange={(ids) => setForm((p) => p && { ...p, platformIds: ids as number[] })}
            />
          </ManageEntityField>

          <ManageEntityField label="Sources" theme={theme}>
            <SearchableMultiSelect
              theme={theme}
              loading={sourcesLoading}
              placeholder="Select sources…"
              searchPlaceholder="Search by code or name…"
              options={sources.map((s) => ({ id: s.id, code: s.name_code, label: s.name }))}
              selectedIds={form.sourceIds}
              onChange={(ids) => setForm((p) => p && { ...p, sourceIds: ids as string[] })}
            />
          </ManageEntityField>

          <ManageEntityField label="Tenant Access" theme={theme}>
            {!form.id ? (
              <div className={`text-[12px] ${c.faint}`}>Save the tenant first, then grant access to specific users.</div>
            ) : (
              <TenantAccessPanel tenantId={form.id} theme={theme} />
            )}
          </ManageEntityField>

          <ManageEntityField label="Dashboard Access" theme={theme}>
            {!form.id ? (
              <div className={`text-[12px] ${c.faint}`}>Save the tenant first, then choose which dashboards it can see.</div>
            ) : (
              <TenantDashboardAccessPanel tenantId={form.id} theme={theme} />
            )}
          </ManageEntityField>

          <ManageEntityField label="Status" theme={theme}>
            <button
              type="button"
              onClick={() => setForm((p) => p && { ...p, isActive: !p.isActive })}
              className={`rounded-[4px] border px-3 py-1 text-[12px] font-semibold ${
                form.isActive ? 'border-success bg-success-surface text-success' : 'border-destructive bg-danger-surface text-destructive'
              }`}
            >
              {form.isActive ? 'Active' : 'Inactive'}
            </button>
          </ManageEntityField>
        </>
      )}
    />
  )
}
